import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IssuerSignup = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    organization_name: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/issuer/signup`, formData);
      localStorage.setItem('issuer_token', response.data.token);
      localStorage.setItem('issuer_data', JSON.stringify(response.data.issuer));
      localStorage.setItem('organization_name', formData.organization_name);
      toast.success("Account created successfully!");
      navigate("/issuer/dashboard");
    } catch (err) {
      console.error('Signup error:', err.response?.data);
      
      let errorMessage = "Signup failed";
      const detail = err.response?.data?.detail;
      
      if (Array.isArray(detail)) {
        // Pydantic v2 validation errors come as array
        errorMessage = detail.map(e => e.msg || e.message || 'Validation error').join('. ');
      } else if (typeof detail === 'string') {
        errorMessage = detail;
      } else if (detail?.message) {
        errorMessage = detail.message;
      }
      
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="signup-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Settings className="h-10 w-10 text-slate-900" />
            <span className="text-2xl font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Certificate Machine</span>
          </div>
          <h1 className="text-3xl font-medium text-slate-900 mb-2" style={{fontFamily: 'Playfair Display'}}>Create Your Account</h1>
          <p className="text-slate-600">Start issuing professional certificates today</p>
        </div>

        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8" data-testid="signup-form">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="organization" className="text-sm font-medium text-slate-700 mb-1.5 block">Organization Name</Label>
              <Input
                id="organization"
                type="text"
                value={formData.organization_name}
                onChange={(e) => setFormData({...formData, organization_name: e.target.value})}
                placeholder="Your Company or School"
                required
                className="h-11 rounded-lg border-slate-200"
                data-testid="organization-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-1.5 block">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                placeholder="you@example.com"
                required
                className="h-11 rounded-lg border-slate-200"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-sm font-medium text-slate-700 mb-1.5 block">Password</Label>
              <Input
                id="password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                placeholder="••••••••"
                required
                minLength={8}
                className="h-11 rounded-lg border-slate-200"
                data-testid="password-input"
              />
              <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                <p className="text-xs font-medium text-slate-600 mb-1.5">Password requirements:</p>
                <ul className="text-xs text-slate-500 space-y-1">
                  <li className={formData.password.length >= 8 ? "text-green-600" : ""}>
                    {formData.password.length >= 8 ? "✓" : "○"} At least 8 characters
                  </li>
                  <li className={/[A-Z]/.test(formData.password) ? "text-green-600" : ""}>
                    {/[A-Z]/.test(formData.password) ? "✓" : "○"} One uppercase letter
                  </li>
                  <li className={/[a-z]/.test(formData.password) ? "text-green-600" : ""}>
                    {/[a-z]/.test(formData.password) ? "✓" : "○"} One lowercase letter
                  </li>
                  <li className={/[0-9]/.test(formData.password) ? "text-green-600" : ""}>
                    {/[0-9]/.test(formData.password) ? "✓" : "○"} One number
                  </li>
                </ul>
              </div>
            </div>

            {/* Terms Agreement Checkbox */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="terms"
                checked={agreedToTerms}
                onChange={(e) => setAgreedToTerms(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-slate-500"
                data-testid="terms-checkbox"
              />
              <label htmlFor="terms" className="text-sm text-slate-600 leading-relaxed">
                I agree to the{" "}
                <a href="/terms" target="_blank" className="text-blue-600 hover:underline">Terms of Service</a>
                {" "}and{" "}
                <a href="/privacy" target="_blank" className="text-blue-600 hover:underline">Privacy Policy</a>
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading || !agreedToTerms}
              className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-lg py-3 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              data-testid="signup-submit-btn"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Account"}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-sm text-slate-600">
              Already have an account?{" "}
              <button onClick={() => navigate("/issuer/login")} className="text-blue-600 hover:underline" data-testid="login-link">
                Sign in
              </button>
            </p>
          </div>
        </div>

        <div className="mt-6 text-center">
          <button onClick={() => navigate("/")} className="text-sm text-slate-500 hover:text-slate-700" data-testid="back-home-link">
            ← Back to home
          </button>
        </div>
      </div>
    </div>
  );
};

export default IssuerSignup;
